import { QWIKBOX_STORAGE_BASE_URL } from 'src/app/constants/storage.constants';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Input, isDevMode, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AthleteMetrics } from 'src/app/models/athlete-metrics';
import { TeamSettings } from 'src/app/models/team';
import { User } from 'src/app/models/user';
import { ApiHelperService } from 'src/app/services/api-helper.service';
import { AthletemetricsService } from 'src/app/services/athletemetrics.service';
import { QwikboxService } from 'src/app/services/qwikbox.service';
import { TeamService } from 'src/app/services/team.service';
import { TeamsettingsService } from 'src/app/services/teamsettings.service';
import { UserService } from 'src/app/services/user.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-user-hub-settings',
  templateUrl: './user-hub-settings.component.html',
  styleUrls: ['./user-hub-settings.component.css']
})
export class UserHubSettingsComponent implements OnInit, OnChanges {
  
  @Input() athlete: User;
  @Input() metrics: AthleteMetrics
  @Output() metricsChange: EventEmitter<AthleteMetrics> = new EventEmitter<AthleteMetrics>();

  loadingMetrics: boolean = false;
  uploadingBanner: boolean = false;
  uploadingLogo: boolean = false;
  uploadProgress: string = "0";
  hubPrimaryColor: string = '#507c23';
  hubSecondaryColor: string = '#00000080';
  teamSettings: TeamSettings;

  constructor(
    private userService: UserService, 
    private teamService: TeamService,
    private teamSettingsService: TeamsettingsService,
    private metricsService: AthletemetricsService, 
    private helperService: ApiHelperService, 
    private qwikboxService: QwikboxService
  ) { }

  ngOnInit(): void {    
    this.loadTeamHubSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['athlete'] || changes['metrics']) {
      this.loadTeamHubSettings();
    }
  }

  loadTeamHubSettings() {
    const teamid = this.metrics?.profile?.current_team_id || this.athlete?.currentUserRole?.teamid;
    if(!teamid) {
      this.setHubColors();
      return;
    }

    this.teamSettingsService.getTeamSettings(teamid).then(res => {
      if(res.success) {
        this.teamSettings = res.result;
      }
      this.setHubColors();
    }).catch(error => {
      console.error(error);
      this.setHubColors();
    });
  }

  setHubColors() {
    const primaryColor = this.teamSettings?.hub?.primaryColor || '#507c23';
    const secondaryColor = this.teamSettings?.hub?.secondaryColor || '#000000';
    this.hubPrimaryColor = primaryColor;
    this.hubSecondaryColor = `${secondaryColor}80`;
  }
  
  getBannerImage(): string {
    const parentFolder = isDevMode() ? 'dev_social_media' : "social_media";    

    if(!this.hasBannerImage()) {
      return null;
    }

    return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/${this.athlete._id}/${this.metrics.profile.banner}`;
  }

  hasBannerImage(): boolean {
    return !!this.metrics?.profile?.banner;
  }

  getLogo(): string {
    const defaultImage = "/assets/images/portrait-placeholder.png";
    const parentFolder = isDevMode() ? 'dev_social_media' : "social_media"; 
    
    if(!this.hasProfilePicture()) {
      return defaultImage;
    }

    return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/${this.athlete._id}/${this.metrics.profile.picture}`;
  }

  hasProfilePicture(): boolean {
    return !!this.metrics?.profile?.picture;
  }

  onProfileImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = '/assets/images/portrait-placeholder.png';
  }

  getDefaultBannerLogo(): string {
    if (this.hasTeamLogo()) {
      const parentFolder = isDevMode() ? 'dev_social_media' : "social_media";
      const teamid = this.metrics?.profile?.current_team_id || this.athlete?.currentUserRole?.teamid;
      return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/team/${teamid}/${this.teamSettings.hub.logo}`;
    }

    return '/assets/images/qwikcut-q-logo.png';
  }

  hasTeamLogo(): boolean {
    return !!this.teamSettings?.hub?.logo && !!(this.metrics?.profile?.current_team_id || this.athlete?.currentUserRole?.teamid);
  }

  toggleUploadingSpinner(uploadTypeId: number) {
    //0 = banner image, 1 = logo, future uploads to increase id count
    if(uploadTypeId == 0) {
      this.uploadingBanner = !this.uploadingBanner;
    } else if (uploadTypeId == 1) {
      this.uploadingLogo = !this.uploadingLogo;
    }

    if(!this.uploadingBanner && !this.uploadingLogo) {
      this.uploadProgress = "0";
    }
  }

  getNameForTitle(): string {
    if (!this.athlete) { return ''; }

    //If window is 768 or less stack it
    const divider = +window.innerWidth > 768 ? " " : "<br>";

    let nick = this.metrics?.nickName ? `${divider}"${this.metrics.nickName}"${divider}` : ' ';
    return this.athlete.firstname + nick + this.athlete.lastname;
  }

  getProfileQuote(): string {
    return this.metrics?.profile?.quote || '';
  }

  onBannerChanage(event: Event, uploadTypeId: number) {

    if(this.uploadingBanner || this.uploadingLogo) { return; }

    const input = event.target as HTMLInputElement;
    if(!input.files || !input.files.length) { return; }
    
    const file = input.files[0];
    const expriration = 43200; //12 hours
    const contentType = file.type;
    const name = uuidv4();
    const parentFolder = isDevMode() ? 'dev_social_media' : "social_media"; 
    const s3Key = `${parentFolder}/${this.athlete._id}/${name}`; // Qwikbox (bucket comes from presigned url call) -> social media (or dev version) folder -> user id -> filename
    
    this.qwikboxService.getPresignedUrlForUpload(s3Key, expriration, contentType).then( result => {
      this.toggleUploadingSpinner(uploadTypeId);
      this.teamService.uploadFiletoS3(result.result, contentType, file).subscribe({
        next: (event: HttpEvent<any>) => {
          
          if(event.type === HttpEventType.UploadProgress) {
            //progress
            this.uploadProgress = ((event.loaded / event.total) * 100).toFixed(2);
          } else if(event.type === HttpEventType.Response) {
            this.toggleUploadingSpinner(uploadTypeId);
            //0 = banner image, 1 = profile, future uploads to increase id count
            if(uploadTypeId == 0) {
              this.metrics.profile.banner = name;
            } else if (uploadTypeId == 1) {
              this.metrics.profile.picture = name;
            }

            this.emitMetricsChange();
            this.saveProfile(false);
          }
        },
        error: (error) => {
          console.error(error);
          this.helperService.showErrorResponse("There was an error uploading your file");
        }
      });
    }).catch(error => {
      console.error(error);
      this.helperService.showErrorResponse("There was an error preparing your file for upload");
    });
    
  }

  emitMetricsChange() {
    this.metricsChange.emit(this.metrics);
  }

  saveProfile(showSuccess: boolean = false) {
    if(!this.metrics?._id) return;
    this.metricsService.updateMetric(this.metrics._id, this.metrics).then(response => {
      if(response.success) {
        if(showSuccess) this.helperService.showSuccessResponse("Profile Updated");
        return;
      }
      this.helperService.showErrorResponse("There was a problem updating your profile.");
    }).catch(error => {
      console.error(error);
      this.helperService.showErrorResponse("There was an error while updating your profile.");
    });
  }

  getDisplayUrl() {
    const uname = this.getStringForRouterLink(`${this.athlete.firstname}-${this.athlete.lastname}`);
    return `${this.getOrigin()}/hub/detail/${this.athlete._id}/${uname}`
  }

  getStringForRouterLink(input: string): string {
    const regex = / /gm;
    return String(input).replace(regex, '-'); //Replace all spaces with dashes, dont' want %20's.
  }

  getOrigin(): string {
    if(window) { return window.location.origin; }
    return ""; // Else no window object, may not be being run in a browser
  }

  copyProfileLink() {
    const text = this.getDisplayUrl();
    const clipBoardElem = document.createElement("input");

    document.body.appendChild(clipBoardElem);
    clipBoardElem.value = text;
    clipBoardElem.select();

    const successfulCopy = document.execCommand('copy');
    document.body.removeChild(clipBoardElem);

    if(successfulCopy) {
      this.helperService.showSuccessResponse("Copied to Clipboard");
    } else {
      this.helperService.showErrorResponse("There was a problem trying to copy this link.");
    }
  }

}
